/**
 * The project layer (RFC 0281 S1b/S2): which projects this window reads, how a
 * pane resolves against its own, and where a new pane lands.
 *
 * These four answers arrived one per slice and lived as four separate `useMemo`
 * blocks strung through the middle of `WorldShell`, interleaved with
 * onboarding and prefetch and keymap. They are one idea, and separating them
 * cost more than it saved: the invariant that binds them — A PANE RESOLVES
 * AGAINST THE PROJECT IT NAMES, NEVER THE WINDOW'S — could only be asserted in
 * prose, because no single unit had all four in scope to be tested.
 *
 * Now it can be. The order below is a dependency chain, which is the other
 * reason these belong together:
 *
 *   catalogIds  every project any leaf names, plus the window's own
 *   catalogs    one project document each, read as a map
 *   resolveFile (project, docId) -> something renderable, or null
 *   rendered    the tree with dead document panes pruned
 *   activeProjectId  where the NEXT pane goes: the focused pane's project
 *
 * `activeProjectId` reads `rendered` rather than `tree` on purpose: a pane that
 * is about to be pruned should not be the one that decides where new work
 * lands.
 */

import { useCallback, useMemo } from 'react'
import {
  type ContentNode,
  leafAt,
  pruneMissing,
} from '../../features/code/contentTree'
import { canEmbedWebPages, webFileFromId } from '../../features/web/webPage'
import { type ProjectCatalog, resolveSubject } from '../window/resolveSubject'
import type { Subject } from '../window/subject'
import type { SpaceTree } from '../window/useLayout'
import { projectIdsOf, useProjectCatalogs } from './useProjectCatalogs'

const NO_HIDDEN: readonly SpaceTree[] = []

export interface ProjectLayer {
  /** One catalog per project any pane names. The missing case is explicit. */
  catalogs: Map<string, ProjectCatalog>
  /** Resolve a doc id against a NAMED project's catalog, or the window's. */
  resolveFile: (
    forProjectId: string | null,
    id: string | null,
  ) => Subject | null
  /** The tree with panes whose document is genuinely gone removed. */
  rendered: ContentNode
  /** The hidden spaces' trees, pruned the same way, in the order given. */
  renderedHidden: SpaceTree[]
  /** The project a NEW pane lands in. */
  activeProjectId: string
}

export function useProjectLayer({
  tree,
  hidden = NO_HIDDEN,
  focusedId,
  fallbackProjectId,
  projectDoc,
}: {
  tree: ContentNode
  /**
   * The trees of the spaces the window is NOT in, which it renders hidden.
   * Their projects are read too, or a hidden document pane would have no
   * catalog to resolve against and draw nothing when its space comes back.
   * Focus and `activeProjectId` never look at them: they are off screen.
   */
  hidden?: readonly SpaceTree[]
  focusedId: string | null
  /** The window's own project: what a pane with no project of its own uses. */
  fallbackProjectId: string
  /**
   * Until this is in, nothing is pruned — absent is not empty (§8).
   *
   * `unknown` because `useDocument` is called untyped upstream and only its
   * PRESENCE is read here: this is the "has the project document arrived at
   * all" gate, not a thing whose fields are inspected.
   */
  projectDoc: unknown
}): ProjectLayer {
  /**
   * Every project to read: the ones panes name, plus the window's own.
   *
   * The window's is included because things opened by the shell still land in
   * it, so its catalog has to be present the moment one does.
   */
  const catalogIds = useMemo(() => {
    const ids = new Set(projectIdsOf(tree))
    for (const space of hidden)
      for (const id of projectIdsOf(space.tree)) ids.add(id)
    ids.add(fallbackProjectId)
    return [...ids].sort()
  }, [tree, hidden, fallbackProjectId])

  const catalogs = useProjectCatalogs(catalogIds)

  const resolveFile = useCallback(
    (forProjectId: string | null, id: string | null): Subject | null =>
      resolveSubject(
        catalogs.get(forProjectId ?? fallbackProjectId) ?? null,
        id,
        {
          webFileFromId,
          canEmbedWeb: canEmbedWebPages(),
        },
      ),
    [catalogs, fallbackProjectId],
  )

  /**
   * A pane whose subject is gone renders as an error, which is worse than not
   * being there — so a deleted document takes its pane with it. Sessions are
   * left alone: the list is authoritative and a session doc may simply not have
   * synced.
   *
   * PER LEAF, against the project that leaf names. A pane on a project whose
   * catalog has not arrived resolves to nothing, so this waits on
   * `catalogs.has(...)` rather than pruning panes that are merely unsynced — a
   * prune is not recoverable, and a slow first read on a second project is
   * exactly when it would otherwise fire.
   */
  const prune = useCallback(
    (node: ContentNode): ContentNode => {
      if (!projectDoc) return node
      return (
        pruneMissing(node, (content) =>
          content.kind === 'document' && catalogs.has(content.projectId)
            ? resolveFile(content.projectId, content.docId) !== null
            : true,
        ) ?? node
      )
    },
    [projectDoc, catalogs, resolveFile],
  )
  const rendered = useMemo(() => prune(tree), [tree, prune])
  // Per tree, so a document gone from one space does not disturb another.
  const renderedHidden = useMemo(
    () => hidden.map((space) => ({ ...space, tree: prune(space.tree) })),
    [hidden, prune],
  )

  /**
   * The project a NEW pane lands in: the focused pane's own.
   *
   * "Open this beside what I am looking at" has to mean the project I am
   * looking at, or opening a file from a pane on project B would silently mint
   * a leaf naming project A, which then resolves against the wrong catalog and
   * prunes itself.
   *
   * A TERMINAL names a machine rather than a project, so a pane focused on one
   * has no project to carry over and this falls back to the window's.
   */
  const activeProjectId = useMemo(() => {
    const focused = focusedId ? leafAt(rendered, focusedId) : null
    const content = focused?.content
    if (content && 'projectId' in content && content.projectId)
      return content.projectId
    return fallbackProjectId
  }, [rendered, focusedId, fallbackProjectId])

  return { catalogs, resolveFile, rendered, renderedHidden, activeProjectId }
}
